import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { styled } from '@mui/material/styles';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    maxWidth: '900px',
    width: '90%',
    maxHeight: '90vh',
    margin: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      margin: theme.spacing(1),
      width: '95%',
      maxHeight: '95vh',
    },
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  color: theme.palette.primary.main,
  marginBottom: theme.spacing(2),
  marginTop: theme.spacing(3),
  '&:first-of-type': {
    marginTop: 0,
  },
}));

const InfoRow = ({ label, value }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  return (
    <Grid container spacing={isMobile ? 1 : 2} sx={{ mb: 1 }}>
      <Grid item xs={isMobile ? 12 : 4}>
        <Typography 
          variant="body2" 
          sx={{ 
            fontWeight: 'medium', 
            color: 'text.secondary',
            ...(isMobile && { fontSize: '0.875rem' })
          }}
        >
          {label}:
        </Typography>
      </Grid>
      <Grid item xs={isMobile ? 12 : 8}>
        <Typography 
          variant="body2"
          sx={{
            ...(isMobile && { 
              fontSize: '0.875rem',
              marginTop: '-4px',
              marginBottom: '8px'
            })
          }}
        >
          {value || 'N/A'}
        </Typography>
      </Grid>
    </Grid>
  );
};

const MembershipApplicationModal = ({ open, onClose, application }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  if (!application) return null;

  // Debug spouse data
  console.log('Modal application data:', {
    hasSpouse: !!application.spouse,
    spouseData: application.spouse,
    spouseFirstName: application.spouse?.first_name,
    spouseLastName: application.spouse?.last_name,
    spouseCondition: application.spouse && (application.spouse.first_name || application.spouse.last_name)
  });

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const formatAddress = (addressObj) => {
    if (!addressObj) return 'N/A';
    const parts = [
      addressObj.address,
      addressObj.apt && `Apt ${addressObj.apt}`,
      addressObj.city,
      addressObj.state,
      addressObj.zip
    ].filter(Boolean);
    return parts.join(', ');
  };

  return (
    <StyledDialog open={open} onClose={onClose} scroll="paper">
      <DialogTitle sx={{ pb: isMobile ? 1 : 2 }}>
        <Typography 
          variant={isMobile ? "h6" : "h5"} 
          component="div"
          sx={{ fontSize: isMobile ? '1.1rem' : '1.5rem' }}
        >
          Membership Application Details
        </Typography>
        <Typography 
          variant="body2" 
          color="text.secondary"
          sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}
        >
          Submitted: {formatDate(application.createdAt)}
        </Typography>
      </DialogTitle>
      
      <DialogContent dividers sx={{ px: isMobile ? 2 : 3, py: isMobile ? 2 : 3 }}>
        {/* Applicant Information */}
        <SectionTitle 
          variant="h6"
          sx={{ fontSize: isMobile ? '1rem' : '1.25rem' }}
        >
          Applicant Information
        </SectionTitle>
        <Box sx={{ mb: 3 }}>
          <InfoRow 
            label="Full Name" 
            value={`${application.applicant?.first_name || ''} ${application.applicant?.middle_name || ''} ${application.applicant?.last_name || ''}`.trim()} 
          />
          <InfoRow label="Date of Birth" value={formatDate(application.applicant?.dob)} />
          <InfoRow label="Email" value={application.applicant?.email} />
          <InfoRow label="Phone" value={application.applicant?.phone} />
          <InfoRow label="Address" value={formatAddress(application.applicant)} />
        </Box>

        <Divider />

        {/* Beneficiary Information */}
        <SectionTitle 
          variant="h6"
          sx={{ fontSize: isMobile ? '1rem' : '1.25rem' }}
        >
          Beneficiary Information
        </SectionTitle>
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 'bold',
              mb: 1,
              fontSize: isMobile ? '0.9rem' : '1rem'
            }}
          >
            Primary Beneficiary 
          </Typography>
          <InfoRow 
            label="Full Name" 
            value={`${application.beneficiary?.first_name || ''} ${application.beneficiary?.middle_name || ''} ${application.beneficiary?.last_name || ''}`.trim()} 
          />
          <InfoRow label="Phone" value={application.beneficiary?.phone} />
          <InfoRow label="Address" value={formatAddress(application.beneficiary)} />

          {application.beneficiary2 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 'bold',
                  mb: 1,
                  fontSize: isMobile ? '0.9rem' : '1rem'
                }}
              >
               Contingent Beneficiary 
              </Typography>
              <InfoRow 
                label="Full Name" 
                value={`${application.beneficiary2?.first_name || ''} ${application.beneficiary2?.middle_name || ''} ${application.beneficiary2?.last_name || ''}`.trim()} 
              />
              <InfoRow label="Phone" value={application.beneficiary2?.phone} />
              <InfoRow label="Address" value={formatAddress(application.beneficiary2)} />
            </>
          )}
        </Box>

        <Divider />

        {/* Spouse Information */}
        {application.spouse && (application.spouse.first_name || application.spouse.last_name) && (
          <>
            <SectionTitle 
              variant="h6"
              sx={{ fontSize: isMobile ? '1rem' : '1.25rem' }}
            >
              Spouse Information
            </SectionTitle>
            <Box sx={{ mb: 3 }}>
              <InfoRow 
                label="Full Name" 
                value={`${application.spouse?.first_name || ''} ${application.spouse?.middle_name || ''} ${application.spouse?.last_name || ''}`.trim()} 
              />
              <InfoRow label="Date of Birth" value={formatDate(application.spouse?.dob)} />
              <InfoRow label="Email" value={application.spouse?.email} />
              <InfoRow label="Phone" value={application.spouse?.phone} />
            </Box>
            <Divider />
          </>
        )}

        {/* Children Information */}
        {application.children && application.children.length > 0 && application.children.some(child => child.first_name || child.last_name) && (
          <>
            <SectionTitle 
              variant="h6"
              sx={{ fontSize: isMobile ? '1rem' : '1.25rem' }}
            >
              Children Information
            </SectionTitle>
            <TableContainer 
              component={Paper} 
              variant="outlined" 
              sx={{ 
                mb: 3,
                overflowX: 'auto',
                '& .MuiTable-root': {
                  minWidth: isMobile ? 'auto' : 650
                }
              }}
            >
              <Table size={isMobile ? "small" : "medium"}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem', fontWeight: 'bold' }}>
                      First Name
                    </TableCell>
                    <TableCell sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem', fontWeight: 'bold' }}>
                      Middle Name
                    </TableCell>
                    <TableCell sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem', fontWeight: 'bold' }}>
                      Last Name
                    </TableCell>
                    <TableCell sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem', fontWeight: 'bold' }}>
                      Relationship
                    </TableCell>
                    <TableCell sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem', fontWeight: 'bold' }}>
                      Gender
                    </TableCell>
                    <TableCell sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem', fontWeight: 'bold' }}>
                      Disabled
                    </TableCell>
                    <TableCell sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem', fontWeight: 'bold' }}>
                      DOB
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {application.children
                    .filter(child => child.first_name || child.last_name)
                    .map((child, index) => (
                      <TableRow key={index}>
                        <TableCell sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>
                          {child.first_name || 'N/A'}
                        </TableCell>
                        <TableCell sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>
                          {child.middle_name || 'N/A'}
                        </TableCell>
                        <TableCell sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>
                          {child.last_name || 'N/A'}
                        </TableCell>
                        <TableCell sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>
                          {child.relationship}
                        </TableCell>
                        <TableCell sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>
                          {child.gender}
                        </TableCell>
                        <TableCell sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>
                          {child.disabled}
                        </TableCell>
                        <TableCell sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>
                          {formatDate(child.dob)}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Divider />
          </>
        )}

        {/* Application Metadata */}
        <SectionTitle 
          variant="h6"
          sx={{ fontSize: isMobile ? '1rem' : '1.25rem' }}
        >
          Application Details
        </SectionTitle>
        <Box>
          <InfoRow label="Application ID" value={application._id} />
          <InfoRow label="Submitted Date" value={formatDate(application.createdAt)} />
          <InfoRow label="Signature Date" value={formatDate(application.signature?.date)} />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: isMobile ? 2 : 3, py: isMobile ? 1.5 : 2 }}>
        <Button 
          onClick={onClose} 
          variant="contained"
          fullWidth={isMobile}
          sx={{ 
            minWidth: isMobile ? 'auto' : '100px',
            fontSize: isMobile ? '0.875rem' : '1rem'
          }}
        >
          Close
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};

export default MembershipApplicationModal;
